// =============================================================================
// AI Charting tab — natural-language charting on demand (R42).
//
// The user describes the chart in plain English; the server (/api/agent/visualise)
// picks a whitelisted chart type + dimensions and computes every figure, then this
// tab renders the returned spec + data via <ChartRenderer>. The AI only chooses the
// shape — it never produces a number — so a chart can never disagree with the
// dashboard (trust model). AI-only: if no model is configured the endpoint returns
// 503 and this tab shows an unavailable state.
//
// One of two sub-tabs on the AI Portal page (see pages/AskPortal.tsx).
// =============================================================================
import { useEffect, useRef, useState } from 'react'
import { Send, BarChart3, Download, HelpCircle, ChevronDown } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { Card, Button, EmptyState } from '../../components/ui'
import { ChartRenderer } from '../../components/ChartRenderer'
import { downloadChartPng } from '../../lib/chartExport'
import { useChartingSlice, setLastChart } from '../../store/usePageSlices'
import type { VisualiseResponse, VisualiseCapabilities, Meta } from '../../lib/types'

// The NL charting input cap is a fixed server-side limit (index.js) — not worth an async round-trip.
const MAX_PROMPT_CHARS = 500

// Curated to show the full range: multi-series line, stacked bar with a split, a filtered view,
// a donut, a sum metric (not count), and the geographic dimension. Each verified to render well.
const SUGGESTIONS = [
  'Migrations by month split by approach',
  'Circuit status split by RAG',
  'At-risk sites by stage',
  'RAG breakdown as a donut',
  'Legacy devices to replace by site size',
  'Geographic spread by postcode area',
]

export function AskPortalChart() {
  // The last built chart + the prompt that produced it persist in the page store so the
  // chart survives navigating away and back. Everything else here is transient per-visit.
  const result = useChartingSlice((s) => s.lastChart) as VisualiseResponse | null
  const persistedPrompt = useChartingSlice((s) => s.lastPrompt)
  const setResult = (r: VisualiseResponse | null, prompt: string) => setLastChart(r, prompt)
  const [input, setInput] = useState(persistedPrompt)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unavailable, setUnavailable] = useState(false)
  const [caps, setCaps] = useState<VisualiseCapabilities | null>(null)
  const [ragColors, setRagColors] = useState<Record<string, string>>({})
  const [ragLabels, setRagLabels] = useState<Record<string, string>>({})
  const [showHelp, setShowHelp] = useState(false)
  const [saving, setSaving] = useState(false)
  const chartRef = useRef<HTMLDivElement>(null)
  // When the current chart was rendered — so Save PNG waits out only the *remaining* entrance
  // animation (instant if the user has been looking at the chart for a moment; brief wait only if
  // they click the instant it appears). Recharts' default entrance animation is ~1.5s.
  const renderedAtRef = useRef(0)

  // Load capabilities (help panel) + RAG colours & labels from meta.ragBands (the RAG authority), so
  // RAG charts match the dashboards in both colour and wording (e.g. Blue → "Migration Activities
  // Completed", unscheduled → "Migration Date Pending").
  useEffect(() => {
    api.agentVisualiseCapabilities().then(setCaps).catch(() => setCaps(null))
    api.meta().then((m: Meta) => {
      const colors: Record<string, string> = {}
      const labels: Record<string, string> = {}
      for (const [token, band] of Object.entries(m.ragBands)) { colors[token] = band.color; labels[token] = band.label }
      setRagColors(colors)
      setRagLabels(labels)
    }).catch(() => { setRagColors({}); setRagLabels({}) })
  }, [])

  // Stamp when a chart actually renders (effects run after paint, so this is when the entrance
  // animation begins) — Save PNG measures its settle wait from here, not from the API return.
  useEffect(() => {
    if (result && result.data.length > 0) renderedAtRef.current = Date.now()
  }, [result])

  async function build(text?: string) {
    const prompt = (text || input).trim()
    if (!prompt || loading) return
    setInput(prompt)
    setShowHelp(false) // collapse the examples/help panel on any submit (example click, Enter, or Send)
    setLoading(true)
    setError(null)
    setUnavailable(false)

    try {
      const res = await api.agentVisualise({ prompt })
      setResult(res, prompt)
    } catch (e) {
      // 503 = no model configured for this environment (AI-only feature, no fallback).
      if (e instanceof ApiError && e.status === 503) {
        setUnavailable(true)
        setResult(null, prompt)
      } else {
        setError(e instanceof ApiError ? e.message : 'Failed to build the chart. Please try again.')
        setResult(null, prompt)
      }
    } finally {
      setLoading(false)
    }
  }

  const ANIM_MS = 1500 // Recharts default entrance animation
  async function savePng() {
    if (!chartRef.current || !result || saving) return
    // Wait out only the animation time still remaining since the chart rendered — instant if the
    // user waited, a short pause only if they click the moment it appears.
    const remaining = Math.max(0, ANIM_MS - (Date.now() - renderedAtRef.current))
    setSaving(true)
    try {
      if (remaining) await new Promise((r) => setTimeout(r, remaining))
      await downloadChartPng(chartRef.current, result.spec.title)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Prompt input — stays fixed (compact) so the result region below gets the room. */}
      <form onSubmit={(e) => { e.preventDefault(); build() }} className="flex-shrink-0 rounded-card border border-line bg-surface px-3 py-2">
        <div className="flex gap-2">
          <input type="text" value={input} onChange={(e) => setInput(e.target.value)} maxLength={MAX_PROMPT_CHARS}
            placeholder="Describe the chart you want — e.g. 'sites by size split by approach'…" disabled={loading}
            className="min-w-0 flex-1 rounded-md border border-line bg-canvas px-3 py-1 text-[13px] outline-none placeholder:text-muted focus:border-brand-600" />
          <Button type="submit" variant="primary" size="sm" disabled={loading || !input.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
        {/* Compact caption row — the examples now live inside the toggle panel so the tile stays short. */}
        <div className="mt-1.5 flex items-center justify-between">
          <p className="text-[10px] text-muted">Chart data is computed by the server from live data — the AI only picks the chart type and grouping. It cannot invent figures.</p>
          {caps && (
            <button type="button" onClick={() => setShowHelp((v) => !v)}
              className="flex flex-shrink-0 items-center gap-1 text-[11px] font-medium text-brand-600 hover:underline">
              <HelpCircle className="h-3 w-3" /> Examples & what I can chart
              <ChevronDown className={`h-3 w-3 transition-transform ${showHelp ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>

        {/* Help panel — server-driven so it never claims a capability the backend lacks. Leads with
            clickable example prompts, then the capability grid. */}
        {showHelp && caps && (
          <div className="mt-2 space-y-3 rounded-md border border-line bg-canvas p-3">
            <div>
              <p className="mb-1.5 text-[11px] font-semibold text-ink">Example prompts (click to run)</p>
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" onClick={() => build(s)} disabled={loading}
                    className="rounded-full border border-line bg-surface px-2.5 py-0.5 text-[11px] text-muted hover:border-brand-600 hover:text-brand-600 disabled:opacity-50">
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 border-t border-line pt-3 text-[11px] sm:grid-cols-2">
            <div>
              <p className="mb-1 font-semibold text-ink">Chart types ({caps.chart_types.length})</p>
              <p className="text-muted">{caps.chart_types.map((c) => c.label).join(' · ')}</p>
            </div>
            <div>
              <p className="mb-1 font-semibold text-ink">Group / split by ({caps.dimensions.length})</p>
              <p className="text-muted">{caps.dimensions.map((d) => d.label).join(' · ')}</p>
            </div>
            <div>
              <p className="mb-1 font-semibold text-ink">Measures</p>
              <p className="text-muted">{caps.metrics.map((m) => m.label).join(' · ')}</p>
            </div>
            <div>
              <p className="mb-1 font-semibold text-ink">Filters</p>
              <p className="text-muted">{caps.filters.map((f) => f.label).join(' · ')}</p>
            </div>
            <p className="text-muted sm:col-span-2">
              Tip: {caps.supports_split ? 'combine a grouping with a split for bar/line/area charts (e.g. “sites by size split by RAG”); pie & donut show a single series. ' : ''}
              Up to {caps.limits.max_categories} categories and {caps.limits.max_series} series per chart.
            </p>
            </div>
          </div>
        )}
      </form>

      {/* Result — the only scrolling region on this tab */}
      <div className="mt-4 min-h-0 flex-1 overflow-auto">
        {loading && (
          <Card className="flex items-center justify-center p-12">
            <span className="inline-flex gap-1"><span className="animate-bounce text-muted">●</span><span className="animate-bounce text-muted" style={{ animationDelay: '0.1s' }}>●</span><span className="animate-bounce text-muted" style={{ animationDelay: '0.2s' }}>●</span></span>
          </Card>
        )}

        {!loading && unavailable && (
          <Card className="p-4">
            <p className="text-[12px] text-rag-amber">AI charting is unavailable in this demo — no Gemini model is configured. Set GCP Vertex/ADC to enable it. The rest of the portal works without it.</p>
          </Card>
        )}

        {!loading && error && (
          <Card className="p-4"><p className="text-[12px] text-rag-red">{error}</p></Card>
        )}

        {!loading && !error && !unavailable && !result && (
          <EmptyState icon="📊" title="Describe a chart to get started" hint="Ask in plain English, or pick one of the examples above." />
        )}

        {!loading && !error && !unavailable && result && (
          result.data.length === 0 ? (
            <Card className="p-4">
              <p className="text-[13px] font-semibold text-ink">{result.spec.title}</p>
              <p className="mt-2 text-[12px] text-muted">{result.note || 'No sites matched — try different filters.'}</p>
            </Card>
          ) : (
            <Card className="p-4">
              <div className="mb-3 flex items-center justify-end">
                <Button size="sm" onClick={savePng} disabled={saving} title="Save chart as PNG">
                  <Download className="h-3.5 w-3.5" /> {saving ? 'Saving…' : 'Save PNG'}
                </Button>
              </div>
              {/* Captured as one image on Save PNG — title, chart + legend, and note together. */}
              <div ref={chartRef} className="bg-surface p-2">
                <div className="mb-2 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-brand-600" />
                  <h3 className="text-[13px] font-semibold text-ink">{result.spec.title}</h3>
                </div>
                <ChartRenderer spec={result.spec} data={result.data} ragColors={ragColors} ragLabels={ragLabels} />
                {result.note && <p className="mt-2 text-[11px] text-muted">{result.note}</p>}
              </div>
            </Card>
          )
        )}
      </div>
    </div>
  )
}
